import { useState } from 'react';
import {
  Alert,
  Button,
  Field,
  Form,
  FormFooter,
  FormRow,
  FormSection,
  type FormSectionStatus,
} from '@evogard/ui';
import { normalizePlate } from '@evogard/domain';
import {
  useCoreVehiclesByPlate,
  useSimilarThirdParties,
  useThirdPartyMutations,
  type CoreVehicleMatch,
  type ThirdPartyVehicle,
} from './useThirdParties.js';

const THIS_YEAR = new Date().getFullYear();

/** Ano de quatro dígitos, de 1900 até o ano que vem (o modelo do ano seguinte já é vendido). */
function parseYear(value: string): number | null {
  if (!/^\d{4}$/.test(value.trim())) return null;
  const year = Number(value);
  return year >= 1900 && year <= THIS_YEAR + 1 ? year : null;
}

/**
 * O veículo do terceiro, vinculado a ele.
 *
 * A placa aparece normalizada enquanto é digitada (`ABC-1D23` → `ABC1D23`),
 * pela mesma `normalizePlate` que o servidor usa — é a forma em que ela é
 * gravada e buscada. O que não normaliza o servidor recusa, e o campo diz
 * antes.
 *
 * **Placa repetida é aviso, não bloqueio** (§7 do plano e decisão de 24/09): o
 * mesmo carro aparece em sinistros diferentes, e pode ter mudado de dono.
 *
 * **O terceiro que também é associado** (item 12): com a placa válida, o campo
 * procura no cadastro da casa (`app.vehicles`) e, se achar, OFERECE o vínculo.
 * Quem cadastra confirma — a placa igual é forte, mas o carro pode ter sido
 * vendido. O aviso continua sendo de terceiro com ou sem o vínculo.
 *
 * A forma é a do formulário do sinistro, como a do `ThirdPartyForm` — ver lá.
 */
export function VehicleForm({
  thirdPartyId,
  ownerName,
  onSaved,
  onCancel,
}: {
  thirdPartyId: number;
  /** O dono, para o título dizer de quem é o carro — no lugar de uma frase solta acima. */
  ownerName?: string;
  onSaved: (vehicle: ThirdPartyVehicle) => void;
  onCancel: () => void;
}) {
  const { createVehicle } = useThirdPartyMutations();
  const [plate, setPlate] = useState('');
  const [model, setModel] = useState('');
  const [manufactureYear, setManufactureYear] = useState('');
  const [modelYear, setModelYear] = useState('');
  const [touched, setTouched] = useState({ plate: false, years: false });
  /* O carro do núcleo escolhido, pela placa em que foi escolhido: trocar a
     placa desfaz a escolha sem precisar de efeito. */
  const [linked, setLinked] = useState<{ plate: string; id: number } | null>(null);

  const normalizedPlate = normalizePlate(plate);
  const coreMatches = useCoreVehiclesByPlate(plate);
  const linkedId = linked && linked.plate === normalizedPlate ? linked.id : null;
  const manufacture = parseYear(manufactureYear);
  const modelY = parseYear(modelYear);
  const { samePlate } = useSimilarThirdParties({ plate }, thirdPartyId);

  const plateError =
    touched.plate && plate.trim() !== '' && !normalizedPlate
      ? 'Três letras e quatro caracteres — ABC1234 ou ABC1D23.'
      : null;
  const yearsError =
    touched.years && manufactureYear && modelYear && (!manufacture || !modelY)
      ? `Ano com quatro dígitos, de 1900 a ${THIS_YEAR + 1}.`
      : manufacture && modelY && modelY < manufacture
        ? 'O ano do modelo não pode ser anterior ao de fabricação.'
        : null;

  /* Os obrigatórios — a mesma lista decide o selo, o rodapé e o envio. */
  const required: Record<string, boolean> = {
    'uma placa válida': normalizedPlate !== null,
    'o modelo': model.trim() !== '',
    'o ano de fabricação': manufacture !== null,
    'o ano do modelo': modelY !== null && (manufacture === null || modelY >= manufacture),
  };
  const missing = Object.keys(required).filter((label) => !required[label]);
  const status: FormSectionStatus = {
    done: Object.keys(required).length - missing.length,
    total: Object.keys(required).length,
    missing,
  };
  const canSubmit = missing.length === 0 && !createVehicle.isPending;

  const submit = () => {
    if (!canSubmit) return;
    createVehicle.mutate(
      {
        thirdPartyId,
        body: {
          plate,
          model: model.trim(),
          manufactureYear: manufacture!,
          modelYear: modelY!,
          vehicleId: linkedId,
        },
      },
      { onSuccess: onSaved },
    );
  };

  return (
    <Form
      steps
      nested
      onSubmit={(e) => {
        e.preventDefault();
        setTouched({ plate: true, years: true });
        submit();
      }}
    >
      {createVehicle.error && <Alert tone="erro">{createVehicle.error.message}</Alert>}

      <FormSection
        icon="veiculo"
        status={status}
        title={ownerName ? `Novo veículo de ${ownerName}` : 'Novo veículo'}
      >
        <FormRow cols={[4, 8]}>
          <Field
            label="Placa"
            required
            autoFocus
            autoComplete="off"
            maxLength={10}
            value={plate}
            onChange={(e) => setPlate(e.target.value.toUpperCase())}
            onBlur={() => setTouched((t) => ({ ...t, plate: true }))}
            placeholder="ABC1D23"
            error={plateError}
            valid={normalizedPlate ? `Gravada como ${normalizedPlate}` : undefined}
          />
          <Field
            label="Modelo"
            required
            maxLength={120}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Marca e modelo, como Chevrolet Onix"
          />
        </FormRow>

        {normalizedPlate && (coreMatches.data?.length ?? 0) > 0 && (
          <CoreMatches
            matches={coreMatches.data!}
            linkedId={linkedId}
            onToggle={(id) =>
              setLinked(id === null ? null : { plate: normalizedPlate, id })
            }
          />
        )}
        {coreMatches.isError && (
          <Alert tone="alerta">
            Não deu para conferir a placa no cadastro da casa: {coreMatches.error.message}
          </Alert>
        )}

        {samePlate.length > 0 && (
          <Alert tone="alerta" title="Esta placa já aparece em outro terceiro">
            {samePlate.map((t) => t.name).join(', ')}{' '}
            {samePlate.length === 1 ? 'tem' : 'têm'} um veículo com esta placa. Pode ser o
            mesmo carro em outro sinistro, ou um carro que trocou de dono — confira antes
            de seguir.
          </Alert>
        )}

        <FormRow cols={[6, 6]}>
          <Field
            label="Ano de fabricação"
            required
            inputMode="numeric"
            maxLength={4}
            value={manufactureYear}
            onChange={(e) => setManufactureYear(e.target.value.replace(/\D/g, ''))}
            onBlur={() => setTouched((t) => ({ ...t, years: true }))}
            placeholder="2022"
          />
          <Field
            label="Ano do modelo"
            required
            inputMode="numeric"
            maxLength={4}
            value={modelYear}
            onChange={(e) => setModelYear(e.target.value.replace(/\D/g, ''))}
            onBlur={() => setTouched((t) => ({ ...t, years: true }))}
            placeholder="2023"
            error={yearsError}
          />
        </FormRow>
      </FormSection>

      <FormFooter>
        <Button type="button" variant="discreto" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          type="submit"
          loading={createVehicle.isPending}
          disabled={createVehicle.isPending}
        >
          Vincular veículo
        </Button>
      </FormFooter>
    </Form>
  );
}

/**
 * Os carros do núcleo com a mesma placa, e a escolha de ligar a um deles. É
 * um aviso de informação, e não de alerta: achar a placa não é problema, é
 * uma pista de que o terceiro também é associado.
 */
function CoreMatches({
  matches,
  linkedId,
  onToggle,
}: {
  matches: CoreVehicleMatch[];
  linkedId: number | null;
  onToggle: (id: number | null) => void;
}) {
  return (
    <Alert tone="informacao" title="Esta placa está no cadastro da casa">
      <span className="flex flex-col gap-2">
        <span>
          {matches.length === 1
            ? 'Um carro de associado tem esta placa.'
            : `${matches.length} carros de associado têm esta placa.`}{' '}
          Se for o mesmo carro, ligue o cadastro: o aviso continua sendo de terceiro, e
          fica registrado quem a pessoa é.
        </span>
        {matches.map((m) => {
          const on = linkedId === m.id;
          return (
            <span
              key={m.id}
              className="flex flex-wrap items-center justify-between gap-2"
            >
              <span className="min-w-0">
                <span className="font-mono tabular-nums">{m.code}</span>
                {' · '}
                {[m.model, m.modelYear].filter(Boolean).join(' · ') ||
                  'modelo não informado'}
                {' · '}
                {m.memberName ?? 'sem nome no cadastro'}
              </span>
              <Button
                type="button"
                size="sm"
                variant={on ? 'primario' : 'contorno'}
                aria-pressed={on}
                onClick={() => onToggle(on ? null : m.id)}
              >
                {on ? 'Ligado — desfazer' : 'É o mesmo carro'}
              </Button>
            </span>
          );
        })}
      </span>
    </Alert>
  );
}
